import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, IMG } from "@/lib/api";
import { PlayCircle, MapPin, BedDouble, Ruler, ArrowLeft, ChevronsUp, Heart, X } from "lucide-react";

const ONBOARDING_KEY = "tlr_discover_seen";

// Auto-rotating photo slideshow for properties without a video walkthrough.
// Runs a Ken Burns-esque zoom + crossfade every ~3.5s.
function PhotoSlideshow({ images, title }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (images.length <= 1) return;
    const iv = setInterval(() => setIdx((i) => (i + 1) % images.length), 3500);
    return () => clearInterval(iv);
  }, [images.length]);
  return (
    <div className="absolute inset-0 overflow-hidden">
      {images.map((path, i) => (
        <img
          key={path + i}
          src={IMG(path)}
          alt={`${title} — ${i + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            i === idx ? "opacity-100 scale-105" : "opacity-0"
          }`}
          style={{ transitionProperty: "opacity, transform", transitionDuration: "700ms, 4000ms" }}
        />
      ))}
      {images.length > 1 && (
        <div className="absolute top-4 inset-x-4 flex gap-1 z-10">
          {images.map((_, i) => (
            <span
              key={i}
              className={`h-0.5 flex-1 rounded-full transition-all ${
                i === idx ? "bg-white" : "bg-white/30"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Convert stored video_url into embed metadata
function embedUrlFor(raw) {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const host = u.hostname.replace("www.", "");
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = u.searchParams.get("v");
      if (id) return { kind: "iframe", src: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&playsinline=1&loop=1&playlist=${id}` };
    }
    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      if (id) return { kind: "iframe", src: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&playsinline=1&loop=1&playlist=${id}` };
    }
    if (host === "instagram.com" && u.pathname.match(/\/(reel|p)\//)) {
      return { kind: "iframe", src: `https://www.instagram.com/${u.pathname.replace(/^\//, "")}embed` };
    }
    if (/\.(mp4|webm|ogg)$/i.test(u.pathname)) {
      return { kind: "video", src: raw };
    }
    return { kind: "iframe", src: raw };
  } catch {
    return null;
  }
}

export default function ReelsPage() {
  const nav = useNavigate();
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIntro, setShowIntro] = useState(() => {
    try { return !localStorage.getItem(ONBOARDING_KEY); } catch { return true; }
  });
  const scrollerRef = useRef(null);

  const dismissIntro = () => {
    try { localStorage.setItem(ONBOARDING_KEY, "1"); } catch {}
    setShowIntro(false);
  };

  useEffect(() => {
    api.get("/properties", { params: { limit: 60 } })
      .then(({ data }) => {
        // Any property that has EITHER a video walkthrough OR at least one
        // photo is a valid "reel". Video wins; otherwise we render an
        // auto-rotating photo slideshow. This way Discover is never empty.
        const usable = (data || []).filter((p) => !!p.video_url || (p.image_paths || []).length > 0);
        // Video-first ordering (walkthroughs are more engaging than photos)
        usable.sort((a, b) => (!!b.video_url) - (!!a.video_url));
        setReels(usable);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 bg-[#0A0507] z-50 overflow-hidden" data-testid="reels-page">
      {/* Top overlay bar */}
      <div className="absolute top-0 inset-x-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent">
        <button
          onClick={() => nav(-1)}
          data-testid="reels-close"
          className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <span className="font-display text-lg tracking-wide text-[#C9A24A]">Discover</span>
        <button
          onClick={() => nav("/properties")}
          data-testid="reels-browse-all"
          className="text-xs text-white/80 hover:text-white uppercase tracking-widest"
        >
          Browse all
        </button>
      </div>

      {/* First-visit onboarding overlay */}
      {showIntro && !loading && reels.length > 0 && (
        <div
          className="absolute inset-0 z-30 bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
          data-testid="discover-intro"
        >
          <div className="max-w-md w-full bg-gradient-to-br from-[#7A1E2A] via-[#5C1420] to-[#3D0D15] rounded-3xl border border-[#C9A24A]/40 p-8 text-white shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9)] fade-in">
            <span className="inline-flex items-center gap-2 bg-[#C9A24A]/20 border border-[#C9A24A]/40 text-[#C9A24A] px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-4">
              <ChevronsUp size={12} /> New way to browse
            </span>
            <h2 className="font-display text-3xl mb-3 leading-tight">
              Just scroll — homes come to you.
            </h2>
            <p className="text-white/85 leading-relaxed mb-6">
              Swipe up to see the next home. Tap the heart to save your favourites, or open a listing to reveal the owner instantly.
            </p>
            <div className="space-y-2.5 mb-7 text-sm text-white/90">
              <div className="flex items-start gap-3">
                <ChevronsUp size={18} className="text-[#C9A24A] mt-0.5 shrink-0" />
                <span>Swipe up or scroll for the next property walkthrough</span>
              </div>
              <div className="flex items-start gap-3">
                <Heart size={18} className="text-[#C9A24A] mt-0.5 shrink-0" />
                <span>Tap the heart on any card to save it to your favourites</span>
              </div>
              <div className="flex items-start gap-3">
                <PlayCircle size={18} className="text-[#C9A24A] mt-0.5 shrink-0" />
                <span>Tap "View full details" to see photos and reveal the owner</span>
              </div>
            </div>
            <button
              onClick={dismissIntro}
              data-testid="discover-intro-continue"
              className="w-full tlr-gold-btn rounded-full h-12 text-base"
            >
              Start scrolling
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="h-full flex items-center justify-center text-white/70">Loading reels…</div>
      ) : reels.length === 0 ? (
        <div className="h-full flex flex-col items-center justify-center text-white/85 px-6 text-center gap-3">
          <PlayCircle size={40} className="text-[#C9A24A]" />
          <p className="font-display text-3xl text-white">Nothing to scroll yet</p>
          <p className="text-sm text-white/60 max-w-sm">
            Owners can add a YouTube, Instagram Reel or MP4 walkthrough when listing a property. As soon as they do, it shows up here — ready to scroll.
          </p>
          <button
            onClick={() => nav("/dashboard")}
            data-testid="discover-empty-cta"
            className="mt-4 tlr-gold-btn rounded-full h-11 px-6 text-sm"
          >
            Add a walkthrough to your listing
          </button>
        </div>
      ) : (
        <div
          ref={scrollerRef}
          className="h-full overflow-y-auto snap-y snap-mandatory"
          data-testid="reels-scroller"
          style={{ scrollbarWidth: "none" }}
        >
          {reels.map((p) => {
            const embed = embedUrlFor(p.video_url);
            const imgs = (p.image_paths || []).slice(0, 5);
            const poster = imgs[0] ? IMG(imgs[0]) : null;
            return (
              <div
                key={p.id}
                className="h-screen w-full snap-start relative flex items-center justify-center bg-black"
                data-testid={`reel-${p.id}`}
              >
                {embed?.kind === "video" ? (
                  <video
                    src={embed.src}
                    poster={poster}
                    autoPlay muted loop playsInline
                    className="max-h-full max-w-full object-contain"
                  />
                ) : embed?.kind === "iframe" ? (
                  <div className="w-full max-w-md aspect-[9/16]">
                    <iframe
                      src={embed.src}
                      title={p.title}
                      allow="autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full border-0"
                    />
                  </div>
                ) : imgs.length > 0 ? (
                  <PhotoSlideshow images={imgs} title={p.title} />
                ) : (
                  poster && <img src={poster} alt={p.title} className="max-h-full max-w-full object-contain opacity-60" />
                )}

                {/* Info overlay */}
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 bg-gradient-to-t from-black via-black/60 to-transparent">
                  <div className="max-w-md">
                    <p className="text-[#C9A24A] text-xs font-bold tracking-widest uppercase mb-2">
                      {p.listing_type === "rent" ? "For Rent" : "For Sale"}
                    </p>
                    <h3 className="font-display text-2xl text-white mb-2 leading-snug">{p.title}</h3>
                    <p className="flex items-center gap-2 text-white/80 text-sm mb-3">
                      <MapPin size={14} /> {p.locality}, {p.city}
                    </p>
                    <div className="flex items-center gap-4 text-white/90 text-sm mb-4">
                      <span className="inline-flex items-center gap-1"><BedDouble size={13} /> {p.bhk} BHK</span>
                      <span className="inline-flex items-center gap-1"><Ruler size={13} /> {p.area_sqft} sqft</span>
                      <span className="font-semibold text-[#C9A24A]">
                        ₹{p.price.toLocaleString("en-IN")}{p.listing_type === "rent" ? "/mo" : ""}
                      </span>
                    </div>
                    <button
                      onClick={() => nav(`/property/${p.id}`)}
                      data-testid={`reel-view-${p.id}`}
                      className="tlr-gold-btn rounded-full h-10 px-5 text-sm"
                    >
                      View full details
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
